import { useState, useEffect } from 'react';
import { Hotel, TravellerInfo } from '../types/Quotation';
import { fetchHotels, HotelsPagination } from '../api/Hotels';

interface UseHotelsResult {
  hotels: Hotel[];
  loading: boolean;
  error?: Error;
  pagination?: HotelsPagination;
}

export function useHotels(
  city: string,
  checkIn: string,
  checkOut: string,
  travellers: TravellerInfo,
  page: number,
  limit: number,
  filters: { StarRating?: number; MealType?: string; HotelName?: string; Amenities?: string[] },
  trigger: number,
  childrenAges?: number[]
): UseHotelsResult {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [pagination, setPagination] = useState<HotelsPagination | undefined>();

  // const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    // Only search after user clicks the Search button (trigger > 0)
    if (!trigger || trigger <= 0) {
      setLoading(false);
      setError(undefined);
      setPagination(undefined);
      setHotels([]);
      return;
    }
    const code = (city || '').trim();
    const hasValidCity = /^(?:[A-Za-z]{3}|\d+)$/.test(code);
    const ci = (checkIn || '').trim();
    const co = (checkOut || '').trim();
    const ciDate = ci ? new Date(ci) : null;
    const coDate = co ? new Date(co) : null;
    const hasValidDates = !!(ciDate && coDate && !isNaN(ciDate.getTime()) && !isNaN(coDate.getTime()) && ciDate < coDate);

    if (!hasValidCity || !hasValidDates) {
      // Do not call API until we have required inputs
      setLoading(false);
      setError(undefined);
      setPagination(undefined);
      setHotels([]);
      return;
    }

    setLoading(true);
    setError(undefined);

    const ages: number[] = Array.isArray(childrenAges)
      ? childrenAges.slice(0, Math.max(0, travellers.children || 0))
      : [];
    const rooms = [
      {
        Adults: travellers.adults,
        Children: travellers.children,
        ChildrenAges: ages,
      },
    ];

    fetchHotels({
      city: code,
      checkIn: ci,
      checkOut: co,
      rooms,
      filters,
      page,
      limit,
    })
      .then(response => {
        setHotels((response && response.items) || []);
        setPagination(response?.pagination);
      })
      .catch(err => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [trigger, page, limit, city, checkIn, checkOut, travellers.adults, travellers.children, childrenAges, filters]);

  return { hotels, loading, error, pagination };
}
